import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import { themeJson } from "../config/surveyTheme.js";
import { surveyConfig } from "../config/surveyConfig.js";
import {
    postOnboardingAnswers,
    postSurveyResultsInBackground
} from "../services/surveyService";
import "survey-core/survey.i18n";
import "survey-core/i18n";
import { registerChoiceItemProperties } from "../survey/registerChoiceItemProperties.js";
import {
    collectEligibilityGateQuestions,
    getDisqualifyingQuestionNames,
    hasDisqualifyingAnswer
} from "../survey/eligibilityGate.js";
import { markdownToHtml } from "../utils/markdown.js";

registerChoiceItemProperties();

function notifyHostMessage(messageObj) {
    const messageJson = JSON.stringify(messageObj);
    const handlerName = messageObj?.handler || "surveyComplete";

    try {
        if (window.SurveyBridge && typeof window.SurveyBridge.postMessage === "function") {
            window.SurveyBridge.postMessage(messageJson);
        }

        if (window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === "function") {
            window.flutter_inappwebview.callHandler(handlerName, messageObj);
        }

        if (window.webkit && window.webkit.messageHandlers) {
            if (window.webkit.messageHandlers[handlerName]) {
                window.webkit.messageHandlers[handlerName].postMessage(messageObj);
            } else if (window.webkit.messageHandlers.surveyComplete) {
                window.webkit.messageHandlers.surveyComplete.postMessage(messageObj);
            }
        }

        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
            window.ReactNativeWebView.postMessage(messageJson);
        }

        if (window.parent && window.parent !== window) {
            window.parent.postMessage(messageObj, "*");
        }
    } catch (error) {
        console.log("Host notify error:", error);
    }
}

function notifyHostCloseWebView(reason, extraPayload = {}) {
    notifyHostMessage({
        type: "close_webview",
        reason,
        surveyId: surveyConfig.surveyId,
        surveyPostId: surveyConfig.surveyPostId,
        requestedAt: new Date().toISOString(),
        ...extraPayload,
    });
}

const translations = {
    uz: {
        title: "Siz haqingizda",
        back: "Orqaga",
        next: "Davom etish"
    },
    ru: {
        title: "О вас",
        back: "Назад",
        next: "Продолжить"
    }
};

const testFeedbackText = {
    uz: {
        completeTitle: "Sinovdan o'tganingiz uchun tashakkur",
        completeSubtitle: "Test yakunlandi. Natijalaringiz saqlandi.",
        wrongTitle: "Noto'g'ri",
        wrongSubtitle: "Sizning javobingiz noto'g'ri. Qayta urinib ko'ring yoki savolni o'tkazib yuboring.",
        finish: "Keyingi",
        skip: "O'tkazib yuborish",
        retry: "Yana urinib ko'ring"
    },
    ru: {
        completeTitle: "Спасибо за прохождение теста",
        completeSubtitle: "Тест завершён. Ваши результаты сохранены.",
        wrongTitle: "Неверно",
        wrongSubtitle: "Ваш ответ неверный. Попробуйте снова или пропустите вопрос.",
        finish: "Далее",
        skip: "Пропустить",
        retry: "Попробовать снова"
    }
};

const eligibilityText = {
    uz: {
        title: "Formula AI hozir siz uchun mos emas",
        description:
            "Bu holatda ozish rejasi individual tibbiy nazoratni talab qilishi mumkin. Xavfsizligingiz uchun Formula AI sizga avtomatik reja tuzmaydi. Holatingiz o'zgarsa, ma'lumotni yangilab qayta boshlashingiz mumkin.",
        acknowledge: "Tushundim",
        changeAnswer: "Ma'lumotni o'zgartirish",
        close: "Yopish"
    },
    ru: {
        title: "Formula AI сейчас вам не подходит",
        description:
            "В этом случае план похудения может потребовать индивидуального медицинского наблюдения. Ради вашей безопасности Formula AI не будет составлять автоматический план. Если ваше состояние изменится, вы сможете обновить данные и начать заново.",
        acknowledge: "Понятно",
        changeAnswer: "Изменить данные",
        close: "Закрыть"
    }
};

const ONBOARDING_PROGRESS_START_PERCENT = 50;
const SURVEY_COMPLETE_HANDLER = "survey_complate";

function isQuestionAnsweredForNavigation(question) {
    if (!question) return false;
    if (!question.isRequired) return true;

    const value = question.value;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim().length > 0;
    if (value === null || value === undefined) return false;
    if (typeof value === "object") return Object.keys(value).length > 0 || question.isAnswered;

    return true;
}

function questionHasAnswerValue(question) {
    const value = question?.value;
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
}

// Snapshot of a gate answer, used to remember *which* answer the user has
// already acknowledged. Picking a different answer produces a different
// snapshot, so the sheet is shown again.
function serializeAnswer(value) {
    if (value === undefined) return "";
    try {
        return JSON.stringify(value ?? null);
    } catch (error) {
        return String(value);
    }
}

function questionHasCorrectAnswer(question) {
    const correct = question?.correctAnswer;
    if (correct === null || correct === undefined) return false;
    if (typeof correct === "string") return correct.trim().length > 0;
    if (Array.isArray(correct)) return correct.length > 0;
    return true;
}

function SuccessBadgeIcon() {
    return (
        <svg width="120" height="120" viewBox="0 0 120 120" fill="none" aria-hidden="true">
            <rect x="38" y="20" width="58" height="58" rx="18" fill="#9BE7AE" fillOpacity="0.5" transform="rotate(10 67 49)" />
            <rect x="28" y="32" width="58" height="58" rx="18" fill="#34C759" />
            <path d="M45 61 L54 70 L71 51" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function WrongBadgeIcon() {
    return (
        <svg width="120" height="120" viewBox="0 0 120 120" fill="none" aria-hidden="true">
            <rect x="38" y="20" width="58" height="58" rx="20" fill="#FFB0C6" fillOpacity="0.55" transform="rotate(-10 67 49)" />
            <rect x="28" y="32" width="58" height="58" rx="22" fill="#F2547D" />
            <path d="M48 52 L66 70 M66 52 L48 70" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function SheetCloseIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
                d="M5 5 L15 15 M15 5 L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
            />
        </svg>
    );
}

function SurveySkeleton({ nextLabel }) {
    return (
        <div className="survey-skeleton" aria-hidden="true">
            <div className="survey-skeleton-progress" />
            <div className="survey-skeleton-title" />
            <div className="survey-skeleton-description" />
            <div className="survey-skeleton-options">
                <div className="survey-skeleton-option" />
                <div className="survey-skeleton-option" />
            </div>
            <button type="button" className="survey-skeleton-button" disabled tabIndex={-1}>
                {nextLabel}
            </button>
        </div>
    );
}

function SurveyLoadError({ onRetry }) {
    return (
        <div className="survey-load-error" role="alert">
            <p className="survey-load-error-title">Survey Not Found or Invalid
                <br />
                Please check the GUID and try again.
            </p>
            <button type="button" className="survey-load-error-retry" onClick={onRetry}>
                Try Again
            </button>
        </div>
    );
}

function SurveyComponent() {
    const layoutRef = useRef(null);
    const isSurveyCompletedRef = useRef(false);
    const surveyTypeRef = useRef("onboarding");
    const bypassNavGateRef = useRef(false);
    const completedDataRef = useRef({});
    const eligibilityGateNamesRef = useRef(new Set());
    const acknowledgedGateAnswersRef = useRef(new Map());
    const completionMetaRef = useRef({});
    const [isSurveyLoading, setIsSurveyLoading] = useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [canSkip, setCanSkip] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [feedbackStatus, setFeedbackStatus] = useState(null);
    const [blockedQuestionName, setBlockedQuestionName] = useState(null);
    const queryParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            lang: params.get("lang") === "ru" ? "ru" : "uz",
            userId: params.get("user-id") || "",
            lessonId: params.get("lesson_id") || "",
            surveyId: params.get("survey_id") || surveyConfig.surveyId,
            sessionId: params.get("session_id") || "",
        };
    }, []);

    const locale = queryParams.lang;
    const survey = useMemo(() => {
        const model = new Model();
        model.applyTheme(themeJson);
        model.questionsOnPageMode = "questionPerPage";
        model.showProgressBar = "top";
        model.progressBarType = "questions";
        model.showQuestionNumbers = "off";
        model.showPrevButton = false;
        model.showTitle = false;
        model.showCompletePage = false;
        model.requiredText = "";
        model.onTextMarkdown.add((sender, options) => {
            const html = markdownToHtml(options.text);
            if (html !== options.text) {
                options.html = html;
            }
        });
        return model;
    }, []);

    const applyProgressWidth = useCallback(() => {
        if (!layoutRef.current) return;
        const { questionCount, answeredQuestionCount } = survey.getProgressInfo();
        const totalQuestions = questionCount > 0 ? questionCount : 1;
        const answeredRatio = Math.min(answeredQuestionCount / totalQuestions, 1);
        const widthPercent = surveyTypeRef.current === "onboarding"
            ? ONBOARDING_PROGRESS_START_PERCENT + answeredRatio * (100 - ONBOARDING_PROGRESS_START_PERCENT)
            : answeredRatio * 100;

        layoutRef.current.style.setProperty("--survey-progress-active-width", `${widthPercent}%`);
    }, [survey]);

    const updateNavigationButtonsState = useCallback(() => {
        const currentQuestion = survey.currentSingleQuestion;
        const canGoForward = isQuestionAnsweredForNavigation(currentQuestion);
        const isQuestionRequired = currentQuestion?.isRequired ?? false;
        setCanSkip(!isQuestionRequired);

        const nextAction = survey.navigationBar.getActionById("sv-nav-next");
        if (nextAction) {
            nextAction.enabled = canGoForward;
        }

        const completeAction = survey.navigationBar.getActionById("sv-nav-complete");
        if (completeAction) {
            completeAction.enabled = canGoForward;
        }

        if (layoutRef.current) {
            const navButtons = layoutRef.current.querySelectorAll(".sd-navigation__next-btn, .sd-navigation__complete-btn");
            navButtons.forEach((buttonElement) => {
                buttonElement.disabled = !canGoForward;
            });
        }
    }, [survey]);

    const scheduleNavigationButtonsStateUpdate = useCallback(() => {
        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(() => {
                updateNavigationButtonsState();
            });
            return;
        }

        setTimeout(updateNavigationButtonsState, 0);
    }, [updateNavigationButtonsState]);

    const handleRetryLoad = useCallback(() => {
        setReloadToken((previousValue) => previousValue + 1);
    }, []);

    // A gate question ("#is_sick") answered with anything but "not_sick" holds
    // the survey back until the user has seen the warning sheet. Acknowledging
    // that exact answer releases the gate; changing the answer re-arms it.
    const getBlockingQuestion = useCallback((question) => {
        if (!question) return null;
        if (!eligibilityGateNamesRef.current.has(question.name)) return null;
        if (!hasDisqualifyingAnswer(question)) return null;

        const acknowledgedAnswer = acknowledgedGateAnswersRef.current.get(question.name);
        if (acknowledgedAnswer !== undefined && acknowledgedAnswer === serializeAnswer(question.value)) {
            return null;
        }

        return question;
    }, []);

    const notifyHostSurveyComplete = useCallback((data, meta = {}) => {
        notifyHostMessage({
            type: SURVEY_COMPLETE_HANDLER,
            handler: SURVEY_COMPLETE_HANDLER,
            surveyId: queryParams.surveyId,
            surveyPostId: surveyConfig.surveyPostId,
            data: data || {},
            notEligible: false,
            notEligibleQuestions: [],
            ...meta,
            completedAt: new Date().toISOString(),
            shouldCloseWebView: true,
        });
        notifyHostCloseWebView("survey_completed", {
            handler: SURVEY_COMPLETE_HANDLER,
            shouldCloseWebView: true,
        });
    }, [queryParams.surveyId]);

    useEffect(() => {
        survey.locale = locale;
        survey.pageNextText = translations[locale].next;
        survey.completeText = translations[locale].next;
    }, [locale, survey]);

    useEffect(() => {
        let isMounted = true;

        const logLessonProgress = async () => {
            if (!queryParams.userId || !queryParams.lessonId) {
                console.log("Skipping log_lesson_progress: missing userId or lessonId");
                return;
            }

            try {
                const response = await fetch(
                    "https://api.admin.u-code.io/v2/invoke_function/formula-function-gateway/?project-id=592e6339-d867-489e-8e6a-74ea28e0818d",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "environment-id": "86bbff40-93c1-4af4-88fb-33b1e908f6cc"
                        },
                        body: JSON.stringify({
                            data: {
                                method: "log_lesson_progress",
                                object_data: {
                                    lesson_id: queryParams.lessonId,
                                    user_id: queryParams.userId
                                }
                            }
                        })
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                console.log("log_lesson_progress success");
            } catch (error) {
                console.error("log_lesson_progress error:", error);
            }
        };

        const handleComplete = (sender) => {
            isSurveyCompletedRef.current = true;
            const completedData = sender.data || {};
            // The user may finish the onboarding after acknowledging the gate,
            // so the host is told not to build an automatic plan for them.
            const notEligibleQuestions = getDisqualifyingQuestionNames(
                sender,
                eligibilityGateNamesRef.current
            );
            completionMetaRef.current = {
                notEligible: notEligibleQuestions.length > 0,
                notEligibleQuestions,
            };
            postSurveyResultsInBackground(completedData, surveyConfig.surveyPostId).catch((error) => {
                console.log("Survey post API error:", error);
            });
            postOnboardingAnswers(sender).catch((error) => {
                console.log("Custom onboarding API error:", error);
            });
            logLessonProgress().catch((error) => {
                console.log("Log lesson progress API error:", error);
            });
            console.log(JSON.stringify(completedData, null, 3));

            // For a test, show the completion screen and defer the host close
            // until the user taps its button. Onboarding closes immediately.
            if (surveyTypeRef.current === "test") {
                completedDataRef.current = completedData;
                setFeedbackStatus("complete");
                return;
            }

            notifyHostSurveyComplete(completedData, completionMetaRef.current);
        };

        const handleProgressChanged = () => {
            applyProgressWidth();
            scheduleNavigationButtonsStateUpdate();
        };

        const handleEligibilityValueChanged = (sender, options) => {
            const question = options?.question
                || (options?.name ? sender.getQuestionByName(options.name) : null);

            if (getBlockingQuestion(question)) {
                setBlockedQuestionName(question.name);
            }
        };

        const handleAfterRender = () => {
            scheduleNavigationButtonsStateUpdate();
        };

        const handleAfterRenderQuestion = (sender, options) => {
            const question = options.question;
            if (question && question.isRequired === undefined) {
                question.isRequired = false;
            }
            scheduleNavigationButtonsStateUpdate();
        };

        const shouldBlockForWrongAnswer = (question) => {
            if (!question || !questionHasCorrectAnswer(question)) return false;
            // An unanswered (or skipped) graded question is not graded.
            if (!questionHasAnswerValue(question)) return false;
            return !question.isAnswerCorrect();
        };

        const handleCurrentPageChanging = (sender, options) => {
            // The eligibility gate outranks every other rule, including the
            // programmatic bypass below: a disqualified user never moves on.
            if (options.isGoingForward) {
                const blockingQuestion = getBlockingQuestion(options.oldCurrentQuestion);
                if (blockingQuestion) {
                    bypassNavGateRef.current = false;
                    options.allow = false;
                    setBlockedQuestionName(blockingQuestion.name);
                    return;
                }
            }

            // Let programmatic navigation (wrong-answer "skip") pass through.
            if (bypassNavGateRef.current) {
                bypassNavGateRef.current = false;
                return;
            }

            // Grading only applies to the "test" survey type, moving forward.
            if (surveyTypeRef.current !== "test") return;
            if (!options.isGoingForward) return;

            // A correct answer just advances; a wrong one is held back for retry/skip.
            if (shouldBlockForWrongAnswer(options.oldCurrentQuestion)) {
                options.allow = false;
                setFeedbackStatus("wrong");
            }
        };

        const handleCompleting = (sender, options) => {
            // The gate is re-checked here because the last question of the
            // survey never fires a page change.
            const blockingQuestion = getBlockingQuestion(survey.currentSingleQuestion);
            if (blockingQuestion) {
                bypassNavGateRef.current = false;
                options.allow = false;
                setBlockedQuestionName(blockingQuestion.name);
                return;
            }

            // The last question never fires a page change, so grade it here.
            if (bypassNavGateRef.current) {
                bypassNavGateRef.current = false;
                return;
            }

            if (surveyTypeRef.current !== "test") return;

            if (shouldBlockForWrongAnswer(survey.currentSingleQuestion)) {
                options.allow = false;
                setFeedbackStatus("wrong");
            }
        };

        survey.onComplete.add(handleComplete);
        survey.onValueChanged.add(handleEligibilityValueChanged);
        survey.onValueChanged.add(handleProgressChanged);
        survey.onCurrentPageChanged.add(handleProgressChanged);
        survey.onCurrentPageChanging.add(handleCurrentPageChanging);
        survey.onCompleting.add(handleCompleting);
        survey.onAfterRenderSurvey.add(handleAfterRender);
        survey.onAfterRenderQuestion.add(handleAfterRenderQuestion);

        const fetchSurveyDefinition = async () => {
            setIsSurveyLoading(true);
            setHasLoadError(false);
            setBlockedQuestionName(null);
            eligibilityGateNamesRef.current = new Set();
            acknowledgedGateAnswersRef.current = new Map();
            completionMetaRef.current = {};

            try {
                const response = await fetch(
                    "https://api.admin.u-code.io/v2/invoke_function/formula-function-gateway/?project-id=592e6339-d867-489e-8e6a-74ea28e0818d",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "environment-id": "86bbff40-93c1-4af4-88fb-33b1e908f6cc"
                        },
                        body: JSON.stringify({
                            data: {
                                method: "get_survey_js_by_id",
                                object_data: {
                                    survey_id: queryParams.surveyId
                                }
                            }
                        })
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                const surveyData = result?.data?.data;
                const surveyJson = surveyData?.questions;
                const surveyType = surveyData?.type || "onboarding";

                if (surveyJson && typeof surveyJson === "object") {
                    surveyTypeRef.current = surveyType;
                    survey.fromJSON(surveyJson);
                    survey.showCompletePage = false;
                    eligibilityGateNamesRef.current = collectEligibilityGateQuestions(survey);
                    applyProgressWidth();
                    scheduleNavigationButtonsStateUpdate();
                } else {
                    throw new Error("Invalid survey data received");
                }
            } catch (error) {
                console.error("Failed to load survey:", error);
                if (isMounted) {
                    setHasLoadError(true);
                }
            } finally {
                if (isMounted) {
                    setIsSurveyLoading(false);
                }
            }
        };

        fetchSurveyDefinition();
        applyProgressWidth();
        scheduleNavigationButtonsStateUpdate();

        return () => {
            isMounted = false;
            survey.onComplete.remove(handleComplete);
            survey.onValueChanged.remove(handleEligibilityValueChanged);
            survey.onValueChanged.remove(handleProgressChanged);
            survey.onCurrentPageChanged.remove(handleProgressChanged);
            survey.onCurrentPageChanging.remove(handleCurrentPageChanging);
            survey.onCompleting.remove(handleCompleting);
            survey.onAfterRenderSurvey.remove(handleAfterRender);
            survey.onAfterRenderQuestion.remove(handleAfterRenderQuestion);
        };
    }, [applyProgressWidth, getBlockingQuestion, notifyHostSurveyComplete, reloadToken, scheduleNavigationButtonsStateUpdate, survey, queryParams]);

    const handleBackClick = useCallback(() => {
        if (isSurveyCompletedRef.current) {
            window.location.reload();
            return;
        }

        const movedToPreviousPage = survey.prevPage();
        if (!movedToPreviousPage) {
            notifyHostCloseWebView("survey_closed", {
                type: "survey_closed",
                handler: "survey_closed",
            });
            window.history.back();
        }
    }, [survey]);

    const advanceOrComplete = useCallback(() => {
        // Bypass the grading gate (page-change or completion) for this one move.
        bypassNavGateRef.current = true;
        if (survey.isLastPage) {
            survey.tryComplete();
        } else {
            survey.nextPage();
        }
    }, [survey]);

    const handleWrongSkip = useCallback(() => {
        setFeedbackStatus(null);
        advanceOrComplete();
    }, [advanceOrComplete]);

    const handleWrongRetry = useCallback(() => {
        const currentQuestion = survey.currentSingleQuestion;
        if (currentQuestion && typeof currentQuestion.clearValue === "function") {
            currentQuestion.clearValue();
        }
        setFeedbackStatus(null);
    }, [survey]);

    const handleTestFinish = useCallback(() => {
        // Keep the completion screen visible while the host closes the webview;
        // clearing it first would flash the empty, completed survey underneath.
        notifyHostSurveyComplete(completedDataRef.current);
    }, [notifyHostSurveyComplete]);

    const handleSkipClick = useCallback(() => {
        const blockingQuestion = getBlockingQuestion(survey.currentSingleQuestion);
        if (blockingQuestion) {
            setBlockedQuestionName(blockingQuestion.name);
            return;
        }

        if (surveyTypeRef.current === "test") {
            advanceOrComplete();
            return;
        }
        survey.nextPage();
    }, [survey, advanceOrComplete, getBlockingQuestion]);

    // "Tushundim" — the user keeps the answer and accepts that no automatic
    // plan will be built, so the gate is released for this answer and the rest
    // of the onboarding continues from the next question.
    const handleEligibilityAcknowledge = useCallback(() => {
        const blockedQuestion = blockedQuestionName
            ? survey.getQuestionByName(blockedQuestionName)
            : survey.currentSingleQuestion;

        if (blockedQuestion) {
            acknowledgedGateAnswersRef.current.set(
                blockedQuestion.name,
                serializeAnswer(blockedQuestion.value)
            );
        }
        setBlockedQuestionName(null);

        // Only auto-advance when the sheet belongs to the question on screen;
        // otherwise dismissing it is enough.
        if (blockedQuestion && blockedQuestion === survey.currentSingleQuestion) {
            advanceOrComplete();
        }
    }, [advanceOrComplete, blockedQuestionName, survey]);

    // "Ma'lumotni o'zgartirish" — clear the answer so the question can be
    // re-taken from a clean state. The acknowledgement is dropped with it, so
    // picking a disqualifying answer again brings the sheet back.
    const handleEligibilityChangeAnswer = useCallback(() => {
        const blockedQuestion = blockedQuestionName
            ? survey.getQuestionByName(blockedQuestionName)
            : survey.currentSingleQuestion;

        if (blockedQuestion) {
            acknowledgedGateAnswersRef.current.delete(blockedQuestion.name);
            if (typeof blockedQuestion.clearValue === "function") {
                blockedQuestion.clearValue();
            }
        }
        setBlockedQuestionName(null);
    }, [blockedQuestionName, survey]);

    // The "X" only dismisses. The answer stays selected and the gate stays
    // closed, so the sheet returns as soon as the user tries to continue.
    const handleEligibilityDismiss = useCallback(() => {
        setBlockedQuestionName(null);
    }, []);

    // Tapping the dimmed backdrop does the same as the "X". The check keeps
    // taps that bubble up from inside the sheet from closing it.
    const handleEligibilityScrimClick = useCallback((event) => {
        if (event.target !== event.currentTarget) return;
        setBlockedQuestionName(null);
    }, []);

    return (
        <div className="survey-layout" ref={layoutRef}>
            <header className="survey-top-header">
                <button
                    type="button"
                    className="survey-back-button"
                    onClick={handleBackClick}
                    aria-label={translations[locale].back}
                >
                    <img src="/chevron-right.svg" alt="" />
                </button>
            
                <div className="survey-top-spacer" aria-hidden="true" />
            </header>
            <div className="survey-content" aria-busy={isSurveyLoading}>
                {isSurveyLoading ? <SurveySkeleton nextLabel={translations[locale].next} /> : null}
                {!isSurveyLoading && hasLoadError ? <SurveyLoadError onRetry={handleRetryLoad} /> : null}
                {!isSurveyLoading && !hasLoadError ? <Survey model={survey} /> : null}
                {!isSurveyLoading && !hasLoadError && canSkip && (
                    <button
                        type="button"
                        className="survey-skip-button"
                        onClick={handleSkipClick}
                    >
                        {locale === "ru" ? "Пропустить" : "O'tkazib yuborish"}
                    </button>
                )}
            </div>

            {blockedQuestionName && (
                <div
                    className="eligibility-sheet-scrim"
                    role="presentation"
                    onClick={handleEligibilityScrimClick}
                >
                    <div
                        className="eligibility-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="eligibility-sheet-title"
                    >
                        <span className="eligibility-sheet__handle" aria-hidden="true" />
                        <button
                            type="button"
                            className="eligibility-sheet__close"
                            onClick={handleEligibilityDismiss}
                            aria-label={eligibilityText[locale].close}
                        >
                            <SheetCloseIcon />
                        </button>
                        <h2 id="eligibility-sheet-title" className="eligibility-sheet__title">
                            {eligibilityText[locale].title}
                        </h2>
                        <p className="eligibility-sheet__subtitle">
                            {eligibilityText[locale].description}
                        </p>
                        <div className="eligibility-sheet__actions">
                            <button
                                type="button"
                                className="eligibility-sheet__primary-btn"
                                onClick={handleEligibilityAcknowledge}
                            >
                                {eligibilityText[locale].acknowledge}
                            </button>
                            <button
                                type="button"
                                className="eligibility-sheet__secondary-btn"
                                onClick={handleEligibilityChangeAnswer}
                            >
                                {eligibilityText[locale].changeAnswer}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {feedbackStatus === "complete" && (
                <div className="test-feedback-screen" role="dialog" aria-modal="true">
                    <div className="test-feedback-screen__body">
                        <div className="test-feedback-screen__icon">
                            <SuccessBadgeIcon />
                        </div>
                        <h2 className="test-feedback-screen__title">
                            {testFeedbackText[locale].completeTitle}
                        </h2>
                        <p className="test-feedback-screen__subtitle">
                            {testFeedbackText[locale].completeSubtitle}
                        </p>
                    </div>
                    <div className="test-feedback-screen__footer">
                        <button
                            type="button"
                            className="test-feedback-primary-btn"
                            onClick={handleTestFinish}
                        >
                            {testFeedbackText[locale].finish}
                        </button>
                    </div>
                </div>
            )}

            {feedbackStatus === "wrong" && (
                <div className="test-feedback-sheet-scrim" role="dialog" aria-modal="true">
                    <div className="test-feedback-sheet">
                        <div className="test-feedback-sheet__icon">
                            <WrongBadgeIcon />
                        </div>
                        <h2 className="test-feedback-sheet__title">
                            {testFeedbackText[locale].wrongTitle}
                        </h2>
                        <p className="test-feedback-sheet__subtitle">
                            {testFeedbackText[locale].wrongSubtitle}
                        </p>
                        <div className="test-feedback-sheet__actions">
                            <button
                                type="button"
                                className="test-feedback-secondary-btn"
                                onClick={handleWrongSkip}
                            >
                                {testFeedbackText[locale].skip}
                            </button>
                            <button
                                type="button"
                                className="test-feedback-primary-btn"
                                onClick={handleWrongRetry}
                            >
                                {testFeedbackText[locale].retry}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SurveyComponent;
